import { useState, useEffect, useContext } from "react"
import { SquarePen, Bell, Users, CreditCard, Settings, X } from "lucide-react"
import { makeStyles } from "tss-react/mui"
import { wmColor } from "themes-lib/wmTheme"
import get from "lodash/get"
import upperCase from "lodash/upperCase"
import toLower from "lodash/toLower"
import {
  LINE_OF_BUSINESS,
  YES,
  NO,
  contractType,
  lobType,
  lob,
  API_STATUSES,
} from "constants-lib/common"
import { getWindow } from "utils-lib/getWindow"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { withLocalization } from "hoc-lib/localization"
import withRouter from "hoc-lib/router"
import Box from "component-lib/Base/components/Box"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import CircularProgress from "component-lib/Base/components/CircularProgress"
import { AuthenticationContext } from "contexts-lib/AuthenticationContext"
import {
  getFormattedAddress,
  getFormattedCustomerSupportPhoneNumber,
} from "utils-lib/strings"
import {
  getSiteSection,
  pushToAdobeDataLayer,
} from "utils-lib/analytics/adobeDataLayer"
import isEmpty from "lodash/isEmpty"
import {
  Link,
  ButtonPrimary,
  GenericModal,
  ButtonSecondary,
} from "../../../../../Base"

const useStyles = makeStyles()((theme) => ({
  profileMenuModal: {
    width: "375px !important",
    padding: "1.5rem",
    paddingTop: "1rem",
    margin: "0px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  popupList: {
    padding: "0px",
    paddingTop: "1rem",
    minWidth: "287px",
    "a.wm-link": {
      fontSize: `1rem`,
      fontFamily: `Maax-Regular`,
      lineHeight: `1.5rem !important`,
      fontWeight: 500,
      color: `${wmColor.green8}!important`,
    },
    "& button.wm-link.cancelService": {
      color: `${wmColor.green8} !important`,
    },
    "& > li": {
      display: "flex",
      alignItems: "center",
      fontFamily: `Maax-Regular`,
      padding: "0",
      margin: "0",
    },
    display: "flex",
    flexDirection: "column",
    gap: ".8rem",
  },
  profileName: {
    fontSize: `1.5rem`,
    fontFamily: `Maax-Bold`,
    lineHeight: `1.8rem`,
    fontWeight: 700,
    margin: 0,
    paddingBottom: ".25rem",
  },
  profileEmail: {
    fontSize: `1rem`,
    fontFamily: `Maax-Regular`,
    lineHeight: `1.5rem`,
    fontWeight: 400,
    color: wmColor.gray11,
    margin: 0,
    paddingBottom: ".25rem",
  },
  menulogout: {
    width: "100%",
  },
  profileCloseModal: {
    top: "16px",
    right: "0px",
  },
  profileIcon: {
    paddingRight: "1rem",
    marginTop: ".5rem",
    color: `${wmColor.green8}!important`,
    "& svg": {
      width: "24px",
      height: "24px",
    },
  },
  modalClass: {
    maxWidth: "34.5rem !important",
    width: "34.5rem !important",
    margin: theme.spacing(0.25),
    [theme.breakpoints.down("sm")]: {
      maxWidth: "90% !important",
    },
  },
  cancelModalUI: {
    borderRadius: `${theme.spacing(1.125)} !important`,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "90% !important",
    },
  },
  cancelTitle: {
    padding: `0 ${theme.spacing(2)}`,
    fontFamily: "Maax-Black",
    marginBottom: theme.spacing(2),
    fontSize: "1.4rem",
    lineHeight: "2rem",
    color: theme.palette.common.black,
    [theme.breakpoints.down("sm")]: {
      fontFamily: "Maax-Bold",
    },
  },
  banner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: `0 ${theme.spacing(2)}`,
  },
  bannerText: {
    fontSize: "0.875rem",
    overflow: "hidden",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: `0 ${theme.spacing(2)}`,
    width: "100%",
    marginBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
  },
  backButton: {
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  primaryButton: {
    fontWeight: "bold",
    minWidth: "12.75rem",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  closeClass: {
    position: `relative`,
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
  profileManageContacts: {
    fontSize: `1rem`,
    fontFamily: `Maax-Regular`,
    lineHeight: `1.5rem !important`,
    fontWeight: 500,
    color: `${wmColor.green8}!important`,
  },
}))

export const strings = {
  en_CA: {
    EDIT_PROFILE_INFO_AND_PASSWORD: `Edit Profile Info and Password`,
    NOTIFICATION_PREFERENCE: `Notification Preferences`,
    MANAGE_CONTACTS: `Manage Your Contacts`,
    MANAGE_ACCOUNT: `Manage Accounts`,
    MANAGE_PAYMENT_METHODS: `Manage Payment Methods`,
    LOG_OUT: `Log Out`,
    CUSTOMER_ID: `Customer ID`,
    CANCEL_SERVICE: `Cancel My Service`,
    ARE_YOU_SURE: `Are you sure?`,
    BANNER_MESSAGE: `If you wish to cancel, an agent will reach out within 1 business day to confirm the details and help complete your request.`,
    KEEP_MY_SERVICE: `No, Keep My Service`,
    CANCEL_MY_SERVICE: `Yes, Cancel My Service`,
    CANCELLATION_REQUEST: `Cancellation Request Received`,
    REQUEST_SUBMITTED_BANNER: `An agent will contact you within 1 business day.`,
    COULD_NOT_PROCESS_CANCELLATION: `We couldn’t process your cancellation request. Please try again.`,
  },
  fr_CA: {
    EDIT_PROFILE_INFO_AND_PASSWORD: `Modifier les informations de profil et le mot de passe`,
    NOTIFICATION_PREFERENCE: ` Préférences de notification`,
    MANAGE_CONTACTS: ` Gérer vos contacts`,
    MANAGE_ACCOUNT: `Gérer les comptes`,
    MANAGE_PAYMENT_METHODS: `Gérer les méthodes de paiement`,
    LOG_OUT: `Se déconnecter`,
    CUSTOMER_ID: `Identifiant client`,
    CANCEL_SERVICE: `Annuler mon service`,
    ARE_YOU_SURE: "Êtes-vous sûr(e)?",
    BANNER_MESSAGE: `Si vous souhaitez annuler, un agent vous entrera en relation sous un jour ouvrable pour confirmer les détails et vous aider à compléter votre demande.`,
    KEEP_MY_SERVICE: `Non, conserver mon service`,
    CANCEL_MY_SERVICE: `Oui, annuler mon service`,
    CANCELLATION_REQUEST: `Demande d’annulation reçue`,
    REQUEST_SUBMITTED_BANNER: `Un agent vous contactera dans un délai d’un jour ouvrable.`,
    COULD_NOT_PROCESS_CANCELLATION: `Nous n'avons pas pu traiter votre demande d'annulation. Veuillez réessayer.`,
  },
}

const ProfileMenuModal = ({
  router,
  firstName,
  emailId,
  mobilePhone,
  selectedAccountDetails,
  profileMenuOpen,
  setProfileMenuOpen,
  siteLanguage,
  strings,
  language,
  getServiceChangeEligibility,
  resetSubmitCancelDetails,
  setCancelMovingButtonClicked,
  userAccount,
  myServices,
  customerContacts,
  cancelService,
  customerSelections,
  setResiLogoutState,
  createContactBackCase,
  getContactBackAttributes,
  isSingleAccount,
  userManageAccount,
}) => {
  const { classes } = useStyles()
  if (!profileMenuOpen) return null

  const {
    LDFFReleaseUpdateNavigationMovingFlow,
    isLDClientReady,
    LDFFReleaseEligiblityEntryMywmDashboard,
  } = useFeatureFlags([
    "LDFFReleaseUpdateNavigationMovingFlow",
    "LDFFReleaseEligiblityEntryMywmDashboard",
  ])

  const isCancelMovingButtonClicked = get(
    cancelService,
    `isCancelMovingButtonClicked`,
    false,
  )

  const customerId = isSingleAccount
    ? userManageAccount?.linkedAccounts[0]?.custAccountId
    : selectedAccountDetails?.custAccountId

  useEffect(() => {
    if (!LDFFReleaseEligiblityEntryMywmDashboard) {
      if (cancelService?.getEligibilityStatus === API_STATUSES?.PENDING) {
        setIsInitialLoading(true)
      } else {
        setIsInitialLoading(false)
        const isEligible = get(
          cancelService,
          "cancelServiceEligibility.isCancelEligible",
          "",
        )
        const category = get(
          cancelService,
          "cancelServiceEligibility.category",
          "",
        )
        const lineOfBusiness = get(
          cancelService,
          "cancelServiceEligibility.lineOfBusiness",
          "",
        )
        setLobResi(upperCase(lineOfBusiness))
        setShouldShowCancelService(isEligible)
        setIsCategory(category)
      }
    }
  }, [
    LDFFReleaseEligiblityEntryMywmDashboard,
    cancelService?.getEligibilityStatus,
  ])

  const ssrWindow = getWindow()
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [shouldShowCancelService, setShouldShowCancelService] = useState(``)
  const [cancelRequestModal, setCancelRequestModal] = useState(false)
  const [cancelRequest, setCancelRequest] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [lobResi, setLobResi] = useState(``)
  const [isCategory, setIsCategory] = useState(``)
  const { logOut } = useContext(AuthenticationContext)
  const languageRoute = getLanguageRoute(siteLanguage.language)
  const profileUrl = `${languageRoute}/mywm/user/profile`
  const notificationPreferenceUrl = `${languageRoute}/mywm/user/preferences/search`
  const notificationPreferenceProfileUrl = `${languageRoute}/mywm/user/preferences/search?customerIdToForward=${customerId}`
  const manageContactsUrl = `${languageRoute}/mywm/user/contacts/verify`
  const manageContactsProfileUrl = `${languageRoute}/mywm/user/contacts/verify?customerIdToForward=${customerId}`
  const manageAccountUrl = `${languageRoute}/mywm/user/accounts/manage`
  const managePaymentMethodsUrl = `${languageRoute}/mywm/user/my-payment/method`

  useEffect(() => {
    if (isLDClientReady) {
      if (LDFFReleaseEligiblityEntryMywmDashboard) {
        if (cancelService?.getEligibilityStatus === API_STATUSES?.PENDING) {
          setIsInitialLoading(true)
        } else {
          setIsInitialLoading(false)
          const isEligible = get(
            cancelService,
            "cancelServiceEligibility.data.isCancelEligible",
            "",
          )
          const category = get(
            cancelService,
            "cancelServiceEligibility.data.category",
            "",
          )
          const lineOfBusiness = get(
            cancelService,
            "cancelServiceEligibility.data.lineOfBusiness",
            "",
          )
          setLobResi(upperCase(lineOfBusiness))
          setShouldShowCancelService(isEligible)
          setIsCategory(category)
        }
      }
    }
  }, [
    isLDClientReady,
    LDFFReleaseEligiblityEntryMywmDashboard,
    cancelService?.getEligibilityStatus,
  ])

  const handleCTAPauseInCancelFlow = async (
    isAddWasteStream = false,
    isPauseService = false,
  ) => {
    const userId = get(userAccount, `userDetails.userId`, ``)
    const lang = siteLanguage.language
    await getServiceChangeEligibility({
      customerId,
      userId,
      lang,
      isAddWasteStream,
      isPauseService,
    })
  }

  // wasteStream  data for analytics
  const getWasteStreamDataForAnalytics = () => {
    const services = get(
      myServices,
      `redesignedDashboardServicesDetailsData[0].services`,
      {},
    )
    const servicesArray = services[Object.keys(services)[0]]

    const filteredWasteStream = servicesArray.map(
      (service) => service.wasteStreamGroupCode,
    )
    return filteredWasteStream
  }
  // serviceDescription  data for analytics

  const getserviceDescriptionData = () => {
    const services = get(
      myServices,
      `redesignedDashboardServicesDetailsData[0].services`,
      {},
    )
    const servicesArray = services[Object.keys(services)[0]]

    const filteredServiceDescription = servicesArray.map(
      (service) => service.serviceDescription,
    )
    return filteredServiceDescription
  }

  const handleCTACancelService = async () => {
    const wasteStream = getWasteStreamDataForAnalytics()
    const serviceDescription = getserviceDescriptionData()
    const additionalData = {
      clicks: {
        object_content: {
          serviceAddress: get(userAccount, `userAddress.formattedAddress`, ``),
          serviceZipCode: get(userAccount, `userAddress.postalCode`, ``),
          lob: cancelService?.cancelServiceEligibility?.data?.lineOfBusiness,
          category: cancelService?.cancelServiceEligibility?.data?.category,
          customerId: cancelService?.cancelServiceEligibility?.data?.customerId,
          wasteStream: wasteStream[0],
          serviceDescription: serviceDescription[0],
        },
      },
      page: {
        siteSection: getSiteSection(language),
        pageUrl: ssrWindow?.location?.href,
      },
    }
    if (
      LDFFReleaseUpdateNavigationMovingFlow &&
      isCancelMovingButtonClicked === true
    ) {
      setCancelMovingButtonClicked(false)
    }

    if (LDFFReleaseEligiblityEntryMywmDashboard) {
      if (!isSingleAccount) {
        pushToAdobeDataLayer("cancelServiceEntryPointMulti", additionalData)
      } else {
        pushToAdobeDataLayer("cancelServiceEntryPointSingle", additionalData)
      }
    }
    resetSubmitCancelDetails()
    if (shouldShowCancelService === YES) {
      const redirectUrl = `${languageRoute}/mywm/user/account/cancel-service`
      router.push(redirectUrl)
    }
  }

  const handleCancelService = async () => {
    setIsLoading(true)
    const isLoggedIn = userAccount?.userDetails?.userId ? NO : YES
    const loggedInData = isLoggedIn
      ? {
          user: {
            id: get(userAccount, `userDetails.userId`, ``),
            login: get(userAccount, `userDetails.login`, ``),
          },
        }
      : {}
    const services =
      myServices?.redesignedDashboardServicesDetailsData?.[0]?.services
    const serviceName = services ? Object.keys(services)[0] : null
    const groupCode = services[serviceName]?.[0]?.wasteStreamGroupCode
    const category = `Change, Add, Pause or Cancel Services`
    const subCategory = [`Cancel Recurring Service`]
    const customerContact =
      customerContacts?.data?.customerContacts?.contacts ?? []
    const serviceMobile = customerContact.find(
      (c) => c.type === "service",
    )?.mobilePhone
    const billingMobile = customerContact.find(
      (c) => c.type === "billing",
    )?.mobilePhone

    let wasteStream
    if (groupCode === "MSW") {
      wasteStream = "Trash service"
    } else if (groupCode === "RECYCLABLE") {
      wasteStream = "Recycling service"
    } else if (groupCode === "ORGANIC") {
      wasteStream = "Organics service"
    } else if (groupCode === "YARD_WASTE") {
      wasteStream = "Yard Waste service"
    }

    let lineOfBusiness = LDFFReleaseEligiblityEntryMywmDashboard
      ? toLower(
          get(
            cancelService,
            "cancelServiceEligibility.data.lineOfBusiness",
            "",
          ),
        )
      : toLower(
          get(cancelService, "cancelServiceEligibility.lineOfBusiness", ""),
        )
    if (
      lineOfBusiness === lob.HOME ||
      lineOfBusiness === toLower(lobType.RESIDENTIAL)
    ) {
      lineOfBusiness = lobType.RESIDENTIAL
    }
    if (
      lineOfBusiness === lob.BUSINESS ||
      lineOfBusiness === toLower(lobType.COMMERCIAL)
    ) {
      lineOfBusiness = lobType.COMMERCIAL
    }

    const data = {
      customer_id: LDFFReleaseEligiblityEntryMywmDashboard
        ? get(cancelService, "cancelServiceEligibility.data.customerId", "")
        : get(cancelService, "cancelServiceEligibility.customerId", ""),
      first_name: get(userAccount, `userDetails.firstName`, ``),
      last_name: get(userAccount, `userDetails.lastName`, ``),
      mobile_phone:
        get(userAccount, `userDetails.mobilePhone`, ``) ||
        get(userAccount, `userDetails.primaryPhone`, ``) ||
        serviceMobile ||
        billingMobile,
      email: get(userAccount, `userDetails.login`, ``),
      category,
      sub_category: Array.isArray(subCategory)
        ? subCategory.join(", ")
        : subCategory,
      lob: lineOfBusiness,
      waste_stream: wasteStream,
      campaign: "Commercial Click 2 Cancel",
      ...loggedInData,
    }

    const contactBackAttributesData = {
      category,
      subCategory,
      lineOfBusiness,
      customerAddress: get(customerSelections, `customerAddress`, ``),
    }

    try {
      await getContactBackAttributes(contactBackAttributesData)
      const response = await createContactBackCase(data)
      if (response?.isAxiosError || response?.response) {
        setIsSubmitted(false)
      } else {
        setIsSubmitted(true)
      }
    } catch (err) {
      setIsSubmitted(false)
    }
    setCancelRequest(true)
    setIsLoading(false)
  }

  const renderHeading = () =>
    isSubmitted && (
      <Box id="modalHeading" className={classes.cancelTitle}>
        {cancelRequest ? strings.CANCELLATION_REQUEST : strings.ARE_YOU_SURE}
      </Box>
    )
  const renderBanner = () => {
    const renderBannerText = isSubmitted
      ? strings.REQUEST_SUBMITTED_BANNER
      : strings.COULD_NOT_PROCESS_CANCELLATION
    return (
      <Box className={classes.banner}>
        <Box className={classes.bannerText}>
          {cancelRequest ? renderBannerText : strings.BANNER_MESSAGE}
        </Box>
      </Box>
    )
  }

  const renderButtons = () =>
    cancelRequest ? null : (
      <Box className={classes.buttonContainer}>
        <ButtonSecondary
          variant="outlined"
          onClick={() => setCancelRequestModal(false)}
          className={classes.backButton}
          ariaLabel="Keep My Service"
        >
          {strings.KEEP_MY_SERVICE}
        </ButtonSecondary>

        <ButtonPrimary
          variant="contained"
          className={classes.primaryButton}
          onClick={handleCancelService}
          ariaLabel="Confirm Cancel My Service"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={19} thickness={6} color="inherit" />
          ) : (
            strings.CANCEL_MY_SERVICE
          )}
        </ButtonPrimary>
      </Box>
    )

  const renderProfileOptions = () => {
    const dashBoardUrl =
      ssrWindow.location.href.endsWith(`/mywm/user/dashboard`)
    const serviceAddress = isSingleAccount
      ? userManageAccount?.linkedAccounts[0]
      : selectedAccountDetails || {}
    const matchedAccount = myServices?.services?.find(
      (account) => account?.ezpayId === customerId,
    )
    const hasPauseService =
      matchedAccount?.serviceChangeEligibility?.pauseService?.eligible

    return (
      <div className="w-100">
        <p className={classes.profileName}>{firstName}</p>
        <div>
          {emailId && <p className={classes.profileEmail}>{emailId}</p>}
          {mobilePhone && (
            <p className={classes.profileEmail}>
              {getFormattedCustomerSupportPhoneNumber(mobilePhone)}
            </p>
          )}
          {!dashBoardUrl && customerId && (
            <p className={classes.profileEmail}>
              {strings.CUSTOMER_ID} : #{customerId}
            </p>
          )}
          {!dashBoardUrl &&
            serviceAddress &&
            !isEmpty(selectedAccountDetails) && (
              <p className={classes.profileEmail}>
                {getFormattedAddress(serviceAddress)}
              </p>
            )}
        </div>
        <ul className={classes.popupList}>
          <li>
            <Link
              href={profileUrl}
              className="d-flex flex-direction-row align-items-center"
              isExternalLink
            >
              <span className={classes.profileIcon}>
                <SquarePen size={18} />
              </span>
              {strings.EDIT_PROFILE_INFO_AND_PASSWORD}
            </Link>
          </li>
          <li>
            <Link
              href={
                dashBoardUrl
                  ? notificationPreferenceUrl
                  : notificationPreferenceProfileUrl
              }
              className="d-flex flex-direction-row align-items-center"
              isExternalLink
            >
              <span className={classes.profileIcon}>
                <Bell size={18} />
              </span>
              {strings.NOTIFICATION_PREFERENCE}
            </Link>
          </li>
          <li>
            <Link
              href={dashBoardUrl ? manageContactsUrl : manageContactsProfileUrl}
              className="d-flex flex-direction-row align-items-center"
              isExternalLink
            >
              <span className={classes.profileIcon}>
                <Users size={18} />
              </span>
              {strings.MANAGE_CONTACTS}
            </Link>
          </li>
          <li>
            <Link
              href={managePaymentMethodsUrl}
              className="d-flex flex-direction-row align-items-center"
              isExternalLink
            >
              <span className={classes.profileIcon}>
                <CreditCard size={18} />
              </span>
              {strings.MANAGE_PAYMENT_METHODS}
            </Link>
          </li>
          <li>
            <Link
              href={manageAccountUrl}
              className="d-flex flex-direction-row align-items-center"
              isExternalLink
            >
              <span className={classes.profileIcon}>
                <Settings size={18} />
              </span>
              {strings.MANAGE_ACCOUNT}
            </Link>
          </li>

          {LDFFReleaseEligiblityEntryMywmDashboard &&
            ((cancelService?.previousSelectedCustomerId !==
              cancelService?.cancelServiceEligibility?.data?.customerId &&
              cancelService?.isResiSaveCancel === true) ||
              cancelService?.isResiSaveCancel === false) &&
            !dashBoardUrl &&
            lobResi === LINE_OF_BUSINESS.RESIDENTIAL &&
            isCategory === contractType.OPEN_MARKET &&
            shouldShowCancelService === YES &&
            !isEmpty(cancelService?.cancelServiceEligibility) && (
              <li>
                <Link
                  analytics={
                    isSingleAccount
                      ? "cancelServiceEntryPointSingle"
                      : "cancelServiceEntryPointMulti"
                  }
                  onClick={() => {
                    if (hasPauseService === YES) {
                      handleCTAPauseInCancelFlow(false, true)
                    }
                    handleCTACancelService()
                  }}
                  className="cancelService d-flex flex-direction-row align-items-center"
                >
                  <span className={classes.profileIcon}>
                    <X size={18} />
                  </span>
                  {strings.CANCEL_SERVICE}
                </Link>
              </li>
            )}
          {!LDFFReleaseEligiblityEntryMywmDashboard &&
            ((cancelService?.previousSelectedCustomerId !==
              cancelService?.cancelServiceEligibility?.customerId &&
              cancelService?.isResiSaveCancel === true) ||
              cancelService?.isResiSaveCancel === false) &&
            !dashBoardUrl &&
            lobResi === LINE_OF_BUSINESS.RESIDENTIAL &&
            isCategory === contractType.OPEN_MARKET &&
            shouldShowCancelService === YES &&
            !isEmpty(cancelService?.cancelServiceEligibility) && (
              <li>
                <Link
                  onClick={() => {
                    if (hasPauseService === YES) {
                      handleCTAPauseInCancelFlow(false, true)
                    }
                    handleCTACancelService()
                  }}
                  className="cancelService d-flex flex-direction-row align-items-center"
                >
                  <span className={classes.profileIcon}>
                    <X size={18} />
                  </span>
                  {strings.CANCEL_SERVICE}
                </Link>
              </li>
            )}

          {isInitialLoading && !dashBoardUrl ? (
            <CircularProgress className="mx-auto" size={24} />
          ) : (
            lobResi === LINE_OF_BUSINESS.COMMERCIAL &&
            isCategory === contractType.OPEN_MARKET &&
            shouldShowCancelService === YES && (
              <li>
                <Link
                  onClick={() => setCancelRequestModal(true)}
                  className="cancelService d-flex flex-direction-row align-items-center"
                  data-testid="CommercialCancelMyServiceLink"
                >
                  <span className={classes.profileIcon}>
                    <X size={18} />
                  </span>
                  {strings.CANCEL_SERVICE}
                </Link>
              </li>
            )
          )}
        </ul>
        <ButtonPrimary
          className="w-100"
          onClick={() => {
            setResiLogoutState()
            logOut()
          }}
        >
          {strings.LOG_OUT}
        </ButtonPrimary>
      </div>
    )
  }

  return (
    <>
      {profileMenuOpen && (
        <GenericModal
          open={profileMenuOpen}
          onClose={() => {
            setProfileMenuOpen(false)
          }}
          customCloseClass={`${classes.profileCloseModal}`}
          customClass={`${classes.profileMenuModal}`}
          useTopRightPosition
          showCloseButton
        >
          {renderProfileOptions()}
        </GenericModal>
      )}
      {cancelRequestModal && (
        <GenericModal
          open={cancelRequestModal}
          onClose={() => {
            setCancelRequestModal(false)
            setCancelRequest(false)
          }}
          showCloseButton
          ariaLabel="Cancel Service Modal"
          data-testid="cancelServiceModal"
          hideButtons
          customClass={classes.modalClass}
          useNarrowContainer={false}
          useFlexibleWidth
          customDialogueClass={classes.cancelModalUI}
          customClassInnerBox="w-100"
          customCloseClass={classes.closeClass}
        >
          {renderHeading()}
          {renderBanner()}
          {renderButtons()}
        </GenericModal>
      )}
    </>
  )
}

export default withLocalization(strings)(withRouter(ProfileMenuModal))
